import { type ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { apiRoutes } from '@/api/routes';
import { useApiErrorMessage } from '@/api/use-api-error';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/design/components/dialog';
import { LoginForm } from '@/features/auth/login';
import { useSessionContext } from '@/features/auth/session';

/**
 * Handing the terminal to the next person, and signing out.
 *
 * **They are two actions, not one with a flag** (AD-54), and they are not variations on each other.
 * Signing out is what somebody does at the end of their shift: the session ends and the terminal
 * returns to the sign-in screen. Handing over is what somebody does mid-shift at a shared terminal
 * with a queue in front of them, and the server makes it ONE act — `auth.switch_operator` verifies
 * the incoming operator's credentials, destroys the outgoing session id in the same `regenerate`
 * that mints the new one, and records its own audit action. So the dialog here is a sign-in for the
 * next person, not a confirmation: there is no moment in between when the terminal belongs to
 * nobody, and a hand-over that fails changes nothing at all.
 *
 * **The sign-out forgets the session on this terminal even when the request fails, and says so.** A
 * terminal that stays signed in because the network blinked is a terminal the next person uses as
 * the last one, which is precisely the misattribution AD-54 exists to stop. The server's session
 * outlives it until its absolute cap, and that is a fact the person walking away is entitled to
 * know, so the failure is reported rather than discarded.
 */

/** Ends the session on this terminal, reporting a failure to end it on the server as well. */
export function useSignOut(): () => void {
  const { client, forget } = useSessionContext();
  const describe = useApiErrorMessage();
  const { t } = useTranslation();

  return useCallback(() => {
    client
      .request(apiRoutes.authLogout, { body: {} })
      .catch((error: unknown) => {
        toast.error(t('session.signOutIncomplete'), { description: describe(error) });
      })
      .finally(forget)
      .catch(() => {
        // `finally` cannot fail here: `forget` is two state setters. The chain is closed rather
        // than left floating so that a rejection can never become an unhandled one.
      });
  }, [client, forget, describe, t]);
}

export function SwitchOperatorDialog({
  open,
  onOpenChange,
}: Readonly<{
  open: boolean;
  onOpenChange: (next: boolean) => void;
}>): ReactNode {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('session.switchOperatorTitle')}</DialogTitle>
          <DialogDescription>{t('session.switchOperatorBody')}</DialogDescription>
        </DialogHeader>
        <LoginForm
          route={apiRoutes.authSwitchOperator}
          onAuthenticated={() => {
            onOpenChange(false);
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
